import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token from store
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth store
      useAuthStore.getState().clearAuth();
      
      const path = window.location.pathname;
      // SÓ Redireciona se estiver tentando entrar em áreas administrativas ou autenticadas
      if (path.startsWith('/admin') || path.startsWith('/superlogin') || path.startsWith('/modo-fabrica') || path.startsWith('/minha-conta')) {
         window.location.href = '/superlogin';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email: string, senha: string) =>
    api.post('/auth/login', { email, senha }),
  
  loginOperador: (matricula: string, pin: string) =>
    api.post('/auth/login-operador', { matricula, pin }),
  
  register: (data: { nome: string; email: string; senha: string; role?: string }) =>
    api.post('/auth/register', data),
  
  me: () => api.get('/auth/me'),
  
  // 2FA
  setup2FA: () => api.post('/auth/2fa/setup'),
  ativar2FA: (token: string) => api.post('/auth/2fa/ativar', { token }),
  verificar2FA: (userId: string, token: string) => api.post('/auth/2fa/verificar', { userId, token }),
};

// Produtos API
export const produtosApi = {
  listar: (params?: { ativo?: boolean; categoriaId?: string; busca?: string }) =>
    api.get('/produtos', { params }),
  
  listarPublico: (params?: { ativo?: boolean; categoriaId?: string; busca?: string }) =>
    api.get('/produtos/publico', { params }),
  
  listarSalaSecreta: (params?: { categoriaId?: string; busca?: string }) =>
    api.get('/produtos/sala-secreta', { params }),
  
  buscar: (id: string) => api.get(`/produtos/${id}`),
  
  buscarPorSlug: (slug: string) => api.get(`/produtos/publico/slug/${slug}`),
  
  criar: (data: any) => api.post('/produtos', data),
  
  atualizar: (id: string, data: any) => api.put(`/produtos/${id}`, data),
  
  deletar: (id: string) => api.delete(`/produtos/${id}`),

  bulkDeletar: (ids: string[]) => api.post('/produtos/bulk-delete', { ids }),
  

  atualizarEstoque: (id: string, operacao: string, quantidade: number) =>
    api.patch(`/produtos/${id}/estoque`, { operacao, quantidade }),
  
  previewCalculo: (data: any) => api.post('/produtos/calculo/preview', data),
  recalcularCusto: (id: string) => api.post(`/produtos/${id}/recalcular-custo`),
  recalcularTodosCustos: () => api.post('/produtos/calculo/recalcular-todos'),
};

// Insumos API
export const insumosApi = {
  listarFilamentos: () => api.get('/insumos/filamentos'),
  criarFilamento: (data: any) => api.post('/insumos/filamentos', data),
  atualizarFilamento: (id: string, data: any) => api.put(`/insumos/filamentos/${id}`, data),
  deletarFilamento: (id: string) => api.delete(`/insumos/filamentos/${id}`),
  bulkDeletarFilamentos: (ids: string[]) => api.post('/insumos/filamentos/bulk-delete', { ids }),
  
  listarEmbalagens: () => api.get('/insumos/embalagens'),
  criarEmbalagem: (data: any) => api.post('/insumos/embalagens', data),
  atualizarEmbalagem: (id: string, data: any) => api.put(`/insumos/embalagens/${id}`, data),
  deletarEmbalagem: (id: string) => api.delete(`/insumos/embalagens/${id}`),
  bulkDeletarEmbalagens: (ids: string[]) => api.post('/insumos/embalagens/bulk-delete', { ids }),
  
  estatisticas: () => api.get('/insumos/estatisticas'),
};

// Pedidos API
export const pedidosApi = {
  listar: (params?: any) => api.get('/pedidos', { params }),
  buscar: (id: string) => api.get(`/pedidos/${id}`),
  criar: (data: any) => api.post('/pedidos', data),
  atualizarStatus: (id: string, status: string) =>
    api.patch(`/pedidos/${id}/status`, { status }),
  atualizarPagamento: (id: string, data: any) =>
    api.patch(`/pedidos/${id}/pagamento`, data),
  dashboardStats: () => api.get('/pedidos/dashboard/stats'),
};

// Produção API
export const producaoApi = {
  listar: (params?: any) => api.get('/producao', { params }),
  buscar: (id: string) => api.get(`/producao/${id}`),
  criar: (data: any) => api.post('/producao', data),
  iniciar: (id: string, operadorId?: string) =>
    api.post(`/producao/${id}/iniciar`, { operadorId }),
  pausar: (id: string, motivo?: string) =>
    api.post(`/producao/${id}/pausar`, { motivo }),
  finalizar: (id: string, data: any) =>
    api.post(`/producao/${id}/finalizar`, data),
  registrarRefugo: (id: string, data: any) =>
    api.post(`/producao/${id}/refugo`, data),
  filaSimplificada: (maquinaId: string) =>
    api.get(`/producao/fila/${maquinaId}`),
  estatisticas: () => api.get('/producao/estatisticas'),
};

// Máquinas API
export const maquinasApi = {
  listar: () => api.get('/maquinas'),
  disponiveis: () => api.get('/maquinas/disponiveis'),
  buscar: (id: string) => api.get(`/maquinas/${id}`),
  criar: (data: any) => api.post('/maquinas', data),
  atualizar: (id: string, data: any) => api.put(`/maquinas/${id}`, data),
  deletar: (id: string) => api.delete(`/maquinas/${id}`),
  atualizarStatus: (id: string, status: string) =>
    api.patch(`/maquinas/${id}/status`, { status }),
  calcularDepreciacao: (id: string) =>
    api.get(`/maquinas/${id}/depreciacao`),
  atualizarConfig: (id: string, data: any) =>
    api.patch(`/maquinas/${id}/config`, data),
  iniciarImpressao: (id: string, data: { produtoId: string, arquivoUrl: string, nomeArquivo: string }) =>
    api.post(`/maquinas/${id}/print`, data),
};

// Frete API
export const freteApi = {
  calcular: (data: { cepDestino: string, volumes: any[] }) =>
    api.post('/frete/calcular', data),
  gerarEtiqueta: (pedidoId: string) =>
    api.post('/frete/gerar-etiqueta', { pedidoId }),
  listarServicos: () => api.get('/frete/servicos'),
  syncTracking: () => api.post('/frete/sync-tracking'),
};

// Cupons API
export const cuponsApi = {
  listar: (params?: any) => api.get('/cupons', { params }),
  buscar: (codigo: string) => api.get(`/cupons/${codigo}`),
  criar: (data: any) => api.post('/cupons', data),
  atualizar: (id: string, data: any) => api.put(`/cupons/${id}`, data),
  deletar: (id: string) => api.delete(`/cupons/${id}`),
  bulkDeletar: (ids: string[]) => api.post('/cupons/bulk-delete', { ids }),
  validar: (codigo: string, data: any) =>
    api.get(`/cupons/validar/${codigo}`, { params: data }),
};

// Clientes API
export const clientesApi = {
  listar: (params?: any) => api.get('/clientes', { params }),
  buscar: (id: string) => api.get(`/clientes/${id}`),
  criar: (data: any) => api.post('/clientes', data),
  atualizar: (id: string, data: any) => api.put(`/clientes/${id}`, data),
  deletar: (id: string) => api.delete(`/clientes/${id}`),
  bulkDeletar: (ids: string[]) => api.post('/clientes/bulk-delete', { ids }),
};

// Categorias API
export const categoriasApi = {
  listar: (params?: any) => api.get('/categorias', { params }),
  listarPublico: () => api.get('/categorias/publico'),
  buscar: (id: string) => api.get(`/categorias/${id}`),
  buscarPorSlug: (slug: string) => api.get(`/categorias/publico/slug/${slug}`),
  criar: (data: any) => api.post('/categorias', data),
  atualizar: (id: string, data: any) => api.put(`/categorias/${id}`, data),
  deletar: (id: string) => api.delete(`/categorias/${id}`),
  bulkDeletar: (ids: string[]) => api.post('/categorias/bulk', { ids }),
};

// Financeiro API
export const financeiroApi = {
  dashboard: () => api.get('/financeiro/dashboard'),
  relatorioCustos: (params?: any) => api.get('/financeiro/relatorio-custos', { params }),
  configuracoes: () => api.get('/financeiro/configuracoes'),
  atualizarConfiguracoes: (data: any) => api.put('/financeiro/configuracoes', data),
};

export default api;
